import { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Container,
  Flex,
  Box,
  Card,
  Text,
  TextField,
  Select,
  Button,
  Tabs,
  RadioCards,
  Inset
} from '@radix-ui/themes';
import { PersonIcon, DotFilledIcon } from '@radix-ui/react-icons';
import { DateTime } from 'luxon';
import { getTeachingPlanCreationData, createTeachingPlan } from '../../../api/courses';
import LoadingIndicator from '../../shared/LoadingIndicator';
import { toast } from 'react-toastify';
import { useSelector, useDispatch } from 'react-redux';
import { updateTeacherTeachingPlans, updateTeacherCourses, updateTeacherPeriods } from '../../../redux/slices/teacherAccountSlice';
import LessonRangeSelector from './LessonRangeSelector';
import { Dot } from 'recharts';

const validationSchema = Yup.object({
  lesson_structure_id: Yup.string().required('Learning plan is required'),
  lessons: Yup.array().min(1, 'At least one lesson must be selected').required('Lessons are required'),
});

const CreateTeachingPlan = ({ course, selectedPeriod = null, onComplete, onCancel }) => {
  const dispatch = useDispatch();
  const [planData, setPlanData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedLessonStructureId, setSelectedLessonStructureId] = useState(null);
  const [selectedLessons, setSelectedLessons] = useState([]);

  const account = useSelector(state => state.account);
  const courses = useSelector(state => state.teacherAccount.courses);
  const periods = useSelector(state => state.teacherAccount.periods);

  if(!selectedPeriod) {
    selectedPeriod = periods[0]
  }

  useEffect(() => {
    const fetchPlanData = async () => {
      setIsLoading(true);
      try {
        const data = await getTeachingPlanCreationData(course.id);
        console.log('data', data);
        if (data.lesson_structures.length > 0) {
          setPlanData(data);
          setSelectedLessonStructureId(data.lesson_structures[0].id);
        } else {
          toast.error('No teaching material available for this course.');
          onCancel();
        }
      } catch (error) {
        console.error('Error fetching plan data:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchPlanData();
  }, []);

  const handleLessonSelectionChange = (lessons) => {
    console.log('form values', formik.values);
    formik.setFieldValue('lessons', lessons);
  };

  const handleComplete = (teachingPlan) => {
        // dispatch(updateTeacherTeachingPlans(teachingPlan));
        const updatedCourses = courses.map(course => {
            if (course.id === teachingPlan.course_id) {
                return {
                    ...course,
                    active_teaching_plan: {
                        ...teachingPlan
                    }
                }
            }
            return course;
        });
        dispatch(updateTeacherCourses(updatedCourses));
        
        // console.log('periods', periods);
        // const updatedPeriods = periods.map(period => {
        //   if(period.id == teachingPlan.period_id){
        //     console.log('pointing to period', period);
        //     return {
        //       ...period, teaching_plans:[...period.teaching_plans, teachingPlan]
        //     }
        //   } else {
        //     return period
        //   }
        // })
        // console.log('updated periods', updatedPeriods);
        // dispatch(updateTeacherPeriods(updatedPeriods));
    onComplete?.(teachingPlan);
  }
  

  const formik = useFormik({
    initialValues: {
      lesson_structure_id: '',
      lessons: []
    },
    validationSchema,
    onSubmit: async (values) => {
      console.log('Form values:', values);
      const data = {
        course_id: course.id,
        period_id: selectedPeriod.id,
        ...values
      }
      try {
        const response = await createTeachingPlan(data);
        console.log('Response:', response.status);
        if (response.status === 'success') {
          toast.success(response.message);
          // dispatch(updateTeacherCourseProperty({ courseId: course.id, property: 'active_teaching_plan', value: response.data }));
          // dispatch(updateTeacherTeachingPlans(response.data.teaching_plan));
          console.log('teaching plan', response.teaching_plan);
          handleComplete(response.teaching_plan);
        }
      } catch (error) {
        console.error('Error submitting form:', error);
        toast.error('Failed to create teaching plan. Please try again.', {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true
        });
      }
    }
  });

  useEffect(() => {
    formik.setFieldValue('lesson_structure_id', selectedLessonStructureId);
  }, [selectedLessonStructureId]);

  if (isLoading) {
    return <LoadingIndicator />;
  }

  const renderGeneralInfo = () => (
    <Flex direction="column" gap="4">
      <Flex direction="row" gap="4">
        <Box>
          <Text as="p" size="2" mb="4">
            Teaching Material
          </Text>
          <Flex direction="column" gap="4">
            {planData?.lesson_structures.map((structure) => (
              <Card key={structure.id} p="0" width="100px" style={{
                width: '100px',
                padding: '5px',
                cursor: 'pointer',
                outline: structure.id === selectedLessonStructureId ? '2px solid var(--accent-8)' : 'none'
              }}
                onClick={() => {
                  setSelectedLessonStructureId(structure.id);
                }}
              >
                <img
                  src={`${import.meta.env.VITE_PUBLIC_URL}${structure.picture}`}
                  alt={structure.name}
                  onError={(e) => (e.currentTarget.src = '/images/edusua_logo_invert.jpg')}
                  style={{
                    width: '100%',
                    height: 'auto',
                    objectFit: 'cover',
                    borderRadius: '3px',
                    marginBottom: '8px'
                  }}
                />
                <Text as="p" size="1" align="center">{structure.name}</Text>
              </Card>
            ))}
          </Flex>
        </Box>
        <Box style={{ flex: 1 }}>
          {formik.touched.lesson_structure_id && formik.errors.lesson_structure_id && (
            <Text color="red" size="2">{formik.errors.lesson_structure_id}</Text>
          )}
        </Box>
      </Flex>
    </Flex>
  );


  return (
    <Flex direction="column" gap="0">
      {!selectedPeriod || periods.length === 0 ? (
        <Text as="p" size="1" mb="4" mr="4">
          No active period
        </Text>
      ):(
        <>
        <Flex direction="row" gap="2" align="center" mb="4">
        <Text>Period(Term)</Text>
         {periods.length > 1 ? (
          <Select.Root defaultValue={selectedPeriod?.id || periods[0].id} onValueChange={(value) => {
            const selectPeriod = periods.find(p => p.id === value);
            if (selectPeriod) {
              selectedPeriod = selectPeriod;
            }
          }}>
            <Select.Trigger />
            <Select.Content>
              {periods.map((period) => (
                <Select.Item key={period.id} value={period.id}>
                  {period.title}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
        ) : (
          <Text size="2" weight="bold" style={{color: 'var(--accent-8)' }}>
            {selectedPeriod.title}
          </Text>
        )}
        </Flex>

      {planData?.lesson_structures?.length > 0 ? (
      <form onSubmit={formik.handleSubmit}>
        <Flex direction="row" gap="4">
          {renderGeneralInfo()}
          <Flex direction="column" gap="4" style={{ flex: 1, maxHeight: '500px', overflowY: 'auto' }}>

            <Text as="p" size="2">
              Lessons
            </Text>
            <LessonRangeSelector
              lessons={planData?.lesson_structures.find(structure =>
                structure.id === selectedLessonStructureId
              )?.lessons}
              onLessonSelectionChange={handleLessonSelectionChange}
              selectedLessonStructureId={selectedLessonStructureId}
            />
          </Flex>
        </Flex>

        <Flex gap="3" mt="4" justify="end">
          <Button variant="soft" color="gray" onClick={onCancel} type="button">Cancel</Button>
          <Button
            type="submit"
            disabled={!formik.isValid || formik.isSubmitting}
          >
            Create Teaching Plan
          </Button>
        </Flex>
      </form>
      ):(
        <Flex direction="column" gap="4">
          <Text as="p" size="2" mb="4">
            No teaching material available for this course.
          </Text>
          <Button variant="soft" color="gray" onClick={onCancel} type="button">Cancel</Button>
        </Flex>
      )}
    </>
  )}
  </Flex>
  )
}
    

export default CreateTeachingPlan;
